import { resolveNodePorts } from '../../Hasp/properties/v2/graphPorts.js';

/**
 * Geometry of graph nodes, ports and wires. All coordinates are in the
 * object layer's space (page origin + page-local node position), so a graph
 * layer shares the stage transform with the objects it connects to.
 */
export const GRAPH_HEADER_HEIGHT = 18;
export const GRAPH_PORT_RADIUS = 5;
// Wide invisible stroke so a thin wire is easy to hit.
export const GRAPH_WIRE_HIT_WIDTH = 12;
// Vertical distance between ports on a node side with more than one port.
export const GRAPH_PORT_PITCH = 18;
// Gap between a UI object's edge and its port, so the port does not cover
// the Transformer's middle resize anchor.
export const GRAPH_OBJECT_PORT_GAP = 2 * GRAPH_PORT_RADIUS;

const NO_PORTS = Object.freeze({ inputs: [], outputs: [] });

/** Height of a node: the domain's node size, grown to fit a side with several ports. */
export function graphNodeHeight(size, ports = NO_PORTS) {
    const count = Math.max(ports.inputs?.length ?? 0, ports.outputs?.length ?? 0);
    return count <= 1 ? size.height : Math.max(size.height, GRAPH_HEADER_HEIGHT + count * GRAPH_PORT_PITCH + 4);
}

/**
 * Point of the `index`-th port on a node side: inputs on the left edge,
 * outputs on the right. A lone port sits halfway down the node; several are
 * spaced out below the header.
 */
export function graphPortPoint(node, origin, size, ports = NO_PORTS, dir = 'in', index = 0) {
    const list = (dir === 'in' ? ports.inputs : ports.outputs) ?? [];
    const y = list.length <= 1
        ? graphNodeHeight(size, ports) / 2
        : GRAPH_HEADER_HEIGHT + (index + 0.5) * GRAPH_PORT_PITCH + 2;
    return { x: origin.x + node.x + (dir === 'in' ? 0 : size.width), y: origin.y + node.y + y };
}

/**
 * Box and port points of a node, from its domain schema.
 * @returns {{ ports, rect, point: (dir: 'in'|'out', portId: string) => {x,y} }}
 */
export function nodeGeometry(node, origin, domain) {
    const ports = resolveNodePorts(domain.getNodeSchema, node);
    const size = domain.nodeSize;
    return {
        ports,
        rect: { x: origin.x + node.x, y: origin.y + node.y, width: size.width, height: graphNodeHeight(size, ports) },
        point: (dir, portId) => {
            const list = (dir === 'in' ? ports.inputs : ports.outputs) ?? [];
            return graphPortPoint(node, origin, size, ports, dir, Math.max(0, list.findIndex(port => port.id === portId)));
        },
    };
}

/** A UI object's port sits just outside its left or right edge, halfway down. */
export function objectPortPoint(rect, side = 'right') {
    return {
        x: side === 'left' ? rect.x - GRAPH_OBJECT_PORT_GAP : rect.x + rect.width + GRAPH_OBJECT_PORT_GAP,
        y: rect.y + rect.height / 2,
    };
}

/**
 * Where a wire end attaches to a UI object `{ rect, ports }`: on the side of
 * its port, else by direction (`dir` 'out' for a wire's start).
 */
export function objectWirePoint(object, portId, dir) {
    const side = object.ports?.find(port => port.id === portId)?.side ?? (dir === 'out' ? 'right' : 'left');
    return objectPortPoint(object.rect, side);
}

/**
 * Cubic Bézier for a wire, as Konva.Line `points` with `bezier: true`.
 * Node-RED style: horizontal tangents whose length grows with the distance;
 * a wire that runs backwards gets longer tangents so it loops around.
 */
export function wireBezierPoints(from, to) {
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const reach = dx >= 0
        ? Math.min(120, Math.max(30, Math.abs(dx) * 0.5))
        : Math.min(160, Math.max(60, Math.abs(dy) * 0.5 + Math.abs(dx) * 0.25));
    return [from.x, from.y, from.x + reach, from.y, to.x - reach, to.y, to.x, to.y];
}
